
import React, { useEffect, useState } from "react";
import { DynamicIcon } from "../components/icons.jsx";
import { LoadingSpinner, Button } from "../components/Common.jsx";

import {
  getOnboardingFull,
  validateTenant,
  saveAuthConfig,
  saveFeatureConfig,
  saveBrandingConfig,
  saveQuotaConfig,
  apiFetch
} from "../api/index.js";

import QuotaConfigForm from "../components/onboarding/QuotaConfigForm.jsx";
import AuthConfigForm from "../components/onboarding/AuthConfigForm.jsx";
import FeatureConfigForm from "../components/onboarding/FeatureConfigForm.jsx";
import BrandingForm from "../components/onboarding/BrandingForm.jsx";
import SupportConfigForm from "../components/onboarding/SupportConfigForm.jsx";
import MfaConfigForm from "../components/onboarding/MfaConfigForm.jsx";
import PlanSelectionForm from "../components/onboarding/PlanSelectionForm.jsx"; // NEW

import { notify } from '@/components/core';

const tabs = [
  { key: "plan", label: "Select Plan", icon: "Layers" }, // NEW
  { key: "auth", label: "Identity & Security", icon: "Shield" },
  { key: "mfa", label: "Multi-Factor Auth", icon: "Lock" },
  { key: "features", label: "Features", icon: "Sparkles" },
  { key: "quotas", label: "Quotas", icon: "BarChart3" },
  { key: "support", label: "Support & SLA", icon: "LifeBuoy" },
  { key: "branding", label: "Branding", icon: "Palette" },
];

export default function TenantOnboardingPage({ token, tenantId, onBack }) {
  const [activeTab, setActiveTab] = useState("plan");
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [tenantId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await getOnboardingFull(tenantId, token);
      setPayload(data);
    } catch (err) {
      notify.error("Failed to load onboarding data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = async (plan) => {
    try {
      setLoading(true);
      // 0. Persist Plan Selection
      await apiFetch(`/tenants/${tenantId}`, token, {
        method: 'PUT',
        body: JSON.stringify({ planId: plan.id })
      });

      // 1. Prepare Feature Defaults
      // Fix: Map Plan human-readable components to DB featureIds
      const planFeaturesArray = plan.features?.components || [];
      const featureDict = {};

      const featureMapping = {
        "Dashboard": ["dashboard"],
        "App Marketplace": ["marketplace"],
        "Creator Studio": ["dashboard_studio", "ml_studio", "genai", "data_models", "ml_engine", "ai_insight"],
      };

      planFeaturesArray.forEach(name => {
        const ids = featureMapping[name];
        if (ids) {
          ids.forEach(id => featureDict[id] = true);
        } else {
          // Fallback: Try snake_case (e.g. "Data Explorer" -> "data_explorer")
          const guessId = name.toLowerCase().replace(/\s+/g, '_');
          featureDict[guessId] = true;
        }
      });

      // Default: Enforce Single Session
      featureDict["single_session"] = true;

      const featureConfig = {
        enabled_features: featureDict
      };

      // 2. Prepare Quota Defaults
      const quotaConfig = {
        api_call_limit: plan.monthly_credits || 10000,
        user_limit: plan.max_users || 10,
      };

      // 3. Prepare Support Defaults
      const supportConfig = {
        support_level: plan.features?.support || "Standard Support"
      };

      // 4. Persist All Defaults to Backend
      // This ensures that subsequent loads (or step validation) see the data.
      await Promise.all([
        saveFeatureConfig(tenantId, token, featureConfig),
        saveQuotaConfig(tenantId, token, quotaConfig),
        saveSupport(supportConfig) // Uses local helper
      ]);

      notify.success(`Plan "${plan.name}" selected. Defaults applied.`);

      // 5. Refresh Payload from Backend to sync state
      await loadData();

      handleNextTab("plan");

    } catch (err) {
      console.error(err);
      notify.error("Failed to apply plan: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveSupport = async (values) => {
    await apiFetch(`/tenant-onboarding/${tenantId}/support`, token, {
      method: 'POST',
      body: JSON.stringify(values)
    });
  };

  const handleSaveTab = async (tabKey, values) => {
    try {
      if (tabKey === "auth") {
        const { single_session, ...authValues } = values;

        // Save Auth Config
        await saveAuthConfig(tenantId, token, authValues);

        // Save Feature Config for Single Session
        const currentFeatures = payload?.features?.enabled_features || {};
        // Only update if single_session is defined in values (meaning it was handled by form)
        if (single_session !== undefined) {
          await saveFeatureConfig(tenantId, token, {
            enabled_features: { ...currentFeatures, single_session }
          });
        }
      }
      else if (tabKey === "features") await saveFeatureConfig(tenantId, token, values);
      else if (tabKey === "quotas") await saveQuotaConfig(tenantId, token, values);
      else if (tabKey === "branding") await saveBrandingConfig(tenantId, token, values);
      else if (tabKey === "support") await saveSupport(values);

      notify.success("Saved successfully!");
      await loadData(); // Refresh payload to update checkmarks
    } catch (err) {
      notify.error("Failed to save data: " + err.message);
      throw err; // Stop propagation to prevent 'Next'
    }
  };

  const handleNextTab = (currentKey) => {
    const idx = tabs.findIndex(t => t.key === currentKey);
    if (idx !== -1 && idx < tabs.length - 1) {
      setActiveTab(tabs[idx + 1].key);
    }
  };

  const handleFinish = async (brandingValues) => {
    setSaving(true);
    try {
      // 1. Save Branding first
      await handleSaveTab("branding", brandingValues);

      // 2. Validate & Activate
      const res = await validateTenant(tenantId, token);
      if (res.status === "success" || res.success) {
        notify.success("Tenant Onboarding Completed Successfully!");
        onBack(); // Go back to list
      } else {
        const errorText = res.errors?.join(", ") || res.message || "Validation failed";
        notify.error("Validation failed, but settings were saved: " + errorText);
      }
    } catch (err) {
      notify.error("Validation error: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Helper to check if a section is "complete" (naive check)
  const isComplete = (key) => {
    if (!payload) return false;
    if (key === 'plan') return !!payload.tenant?.planId;
    if (key === 'auth') return !!payload.auth?.auth_type;
    if (key === 'mfa') return !!payload.auth;
    if (key === 'features') return !!payload.features?.enabled_features;
    if (key === 'quotas') return payload.quota?.api_call_limit !== undefined;
    if (key === 'support') return !!payload.support?.support_tier;
    if (key === 'branding') return !!payload.branding?.id;
    return false;
  }

  const renderActiveTab = () => {
    if (!payload) return null;

    // Common props
    const nextProp = () => handleNextTab(activeTab);

    switch (activeTab) {
      case "plan":
        return <PlanSelectionForm
          token={token}
          onSelect={handlePlanSelect}
          selectedPlanId={payload.tenant?.planId}
        />;
      case "auth":
        return <AuthConfigForm
          data={payload.auth}
          features={payload.features} // Pass Features for Single Session Toggle
          onSave={(v) => handleSaveTab("auth", v)}
          onNext={nextProp}
        />;
      case "mfa":
        return <MfaConfigForm
          data={payload.auth}
          onSave={(v) => handleSaveTab("auth", v)} // Shared endpoint
          onNext={nextProp}
        />;
      case "features":
        return <FeatureConfigForm
          data={payload.features}
          token={token}
          onSave={(v) => handleSaveTab("features", v)}
          onNext={nextProp}
        />;
      case "quotas":
        return <QuotaConfigForm
          data={payload.quota}
          token={token}
          onSave={(v) => handleSaveTab("quotas", v)}
          onNext={nextProp}
        />;
      case "support":
        return <SupportConfigForm
          data={payload.support}
          token={token}
          onSave={(v) => handleSaveTab("support", v)}
          onNext={nextProp}
        />;
      case "branding":
        return <BrandingForm
          tenantId={payload.onboarding?.tenant_id}
          data={payload.branding || {}}
          token={token}
          onSavePartial={(v) => handleSaveTab("branding", v)}
          onSave={(v) => handleFinish(v)} // Special Finish Handler
          isLastStep={true}
          saving={saving}
        />;
      default:
        return null;
    }
  };

  if (loading && !payload) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner size={48} />
      </div>
    );
  }

  return (
    <div className="flex h-full bg-gray-50 dark:bg-gray-900 overflow-hidden font-sans">
      {/* Sidebar */}
      <div className="w-72 bg-white dark:bg-gray-800 border-r dark:border-gray-700 flex flex-col shadow-xl z-10">
        <div className="p-6 border-b dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <Button variant="secondary" onClick={onBack} className="w-full justify-start mb-6 text-gray-600">
            <DynamicIcon name="ArrowLeft" className="mr-2 h-4 w-4" /> Back to Tenants
          </Button>
          <div>
            <h2 className="text-xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-600">Onboarding</h2>

            {payload?.tenant ? (
              <div className="mt-4 mb-2 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700/50 shadow-sm">
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 truncate" title={payload.tenant.name}>
                  {payload.tenant.name}
                </h3>
                {payload.tenant.adminEmail && (
                  <div className="flex items-center text-xs text-gray-500 mt-1.5 truncate" title={payload.tenant.adminEmail}>
                    <DynamicIcon name="Mail" className="w-3.5 h-3.5 mr-1.5 opacity-70" />
                    {payload.tenant.adminEmail}
                  </div>
                )}
                <div className="flex items-center text-xs font-mono text-gray-400 mt-1.5 truncate" title="Tenant ID">
                  <DynamicIcon name="Hash" className="w-3.5 h-3.5 mr-1.5 opacity-70" />
                  {tenantId}
                </div>
              </div>
            ) : (
              <p className="text-xs font-mono text-gray-400 mt-2 break-all bg-gray-100 dark:bg-gray-900 p-1 rounded border dark:border-gray-700">{tenantId}</p>
            )}
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {tabs.map((tab) => {
            const complete = tab.key !== 'review' && isComplete(tab.key);
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 group ${activeTab === tab.key
                  ? "bg-primary text-white shadow-lg shadow-primary/30 transform scale-[1.02]"
                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
              >
                <div className="flex items-center">
                  <DynamicIcon name={tab.icon} className={`mr-3 h-5 w-5 ${activeTab === tab.key ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`} />
                  {tab.label}
                </div>
                {complete && activeTab !== tab.key && <DynamicIcon name="Check" className="w-4 h-4 text-green-500" />}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col bg-gray-50/50 dark:bg-gray-900 overflow-hidden">
        {/* Fixed Header */}
        <div className="shrink-0 pt-8 lg:pt-12 px-8 lg:px-12 pb-4 z-10 bg-gray-50/50 dark:bg-gray-900">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 tracking-tight">
              {tabs.find(t => t.key === activeTab)?.label}
            </h1>
            <p className="text-gray-500 text-lg">Configure settings for this section below.</p>
          </div>
        </div>

        {/* Scrollable Form Area */}
        <div className="flex-1 overflow-auto scroll-smooth pb-12 relative">
          <div className="max-w-7xl mx-auto px-8 lg:px-12 h-full">
            <div className="animate-fade-in h-full flex flex-col">
              {renderActiveTab()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
